import { assertEvent, assign, emit, setup } from 'xstate';
import { AuthTransport } from './transport';
import {
  AuthMachineContext,
  AuthMachineEmittedEvents,
  AuthMachineEvents,
} from './types';
import {
  addError as addErrorAction,
  clearErrors as clearErrorsAction,
  notifyAuthenticated as notifyAuthenticatedAction,
  notifyUnauthenticated as notifyUnauthenticatedAction,
  setAccessToken as setAccessTokenAction,
  setIgnoreRefreshToken as setIgnoreRefreshTokenAction,
  setUserContext as setUserContextAction,
  unsetRefreshToken as unsetRefreshTokenAction,
  unsetUserContext as unsetUserContextAction,
} from './actions';
import {
  authenticate as authenticateActor,
  refreshAccessToken as refreshAccessTokenActor,
  unauthenticate as unauthenticateActor,
} from './actors';

export const authMachine = setup({
  types: {
    context: {} as AuthMachineContext,
    emitted: {} as AuthMachineEmittedEvents,
    events: {} as AuthMachineEvents,
    input: {} as {
      /**
       * Header injection for the auth HTTP calls — see `./transport`.
       * Optional: without it the actors send the historical hardcoded
       * `clicktap-scope: view` / `clicktap-scope-id: default`.
       */
      transport?: AuthTransport;
      initialContext: Partial<AuthMachineContext>;
      initialState?: string;
    },
  },
  actions: {
    addError: assign(addErrorAction),
    clearErrors: assign(clearErrorsAction),
    notifyAuthenticated: emit(notifyAuthenticatedAction),
    notifyUnauthenticated: emit(notifyUnauthenticatedAction),
    setAccessToken: assign(setAccessTokenAction),
    // A credential-change mutation returned a fresh access token (refresh cookie already rotated
    // server-side); swap it into context and re-announce authentication with the new token.
    setReissuedAccessToken: assign(({ event }) => {
      assertEvent(event, 'SESSION_REISSUED');
      return { accessToken: event.accessToken, ignoreRefreshToken: false };
    }),
    notifyReissued: emit(({ event }) => {
      assertEvent(event, 'SESSION_REISSUED');
      return {
        type: 'EMIT_AUTHENTICATED' as const,
        accessToken: event.accessToken,
      };
    }),
    setIgnoreRefreshToken: assign(setIgnoreRefreshTokenAction),
    // Record why the session is ending so `unauthenticating` can route a
    // deliberate sign-out (→ home) apart from an expired session (→ return-path
    // redirect), even though both pass through the same state (CTAP-1129).
    markDeliberateLogout: assign(() => ({
      logoutReason: 'deliberate' as const,
    })),
    markExpiredSession: assign(() => ({ logoutReason: 'expired' as const })),
    setUserContext: assign(setUserContextAction),
    unsetRefreshToken: assign(unsetRefreshTokenAction),
    unsetUserContext: assign(unsetUserContextAction),
  },
  actors: {
    authenticate: authenticateActor,
    refreshAccessToken: refreshAccessTokenActor,
    unauthenticate: unauthenticateActor,
  },
  delays: {
    refresh: 840000,
  },
}).createMachine({
  id: 'auth',
  context: ({ input }) => ({
    transport: input?.transport,
    user: null,
    accessToken: '',
    refreshToken: '', // will only be on the request from the server side for initial check, does not get passed to the client
    /**
     * If unauthenticating fails, we need a way to ignore the refresh token on subsequent access token refresh.
     * This would be set to true done/error of unauthenticate service, and false on done of authenticate service
     */
    ignoreRefreshToken: false,
    logoutReason: null,
    endpoints: {
      login: '',
      logout: '',
      refresh: '',
      ssrRefresh: '',
    },
    errors: [],
    ...input?.initialContext,
  }),
  initial: 'initializing',
  states: {
    initializing: {
      always: [
        {
          target: 'loggedIn',
          guard: ({ context }) => !!context.accessToken,
        },
        {
          target: 'loggedOut.initial',
        },
      ],
    },
    authenticating: {
      invoke: {
        src: 'authenticate',
        input: ({ context, event }) => {
          assertEvent(event, 'LOGIN');

          return {
            endpoint: {
              client: context.endpoints.login,
            },
            transport: context.transport,
            username: event.username,
            password: event.password,
          };
        },
        onDone: [
          {
            actions: ['setUserContext', 'notifyAuthenticated'],
            target: 'loggedIn',
          },
        ],
        onError: [
          {
            actions: 'addError',
            target: 'loggedOut.afterFailedAuth',
          },
        ],
      },
    },
    unauthenticating: {
      invoke: {
        src: 'unauthenticate',
        input: ({ context }) => {
          return {
            endpoint: {
              client: context.endpoints.logout,
            },
            transport: context.transport,
          };
        },
        // `unauthenticating` is reached by a deliberate LOGOUT *and* by a
        // client-side refresh failure (see `refreshing.onError`). Route them
        // apart on `logoutReason`: an expired session goes to
        // `afterRefreshFailure` (→ getRedirectPath keeps the return path), so
        // only a deliberate sign-out lands in `afterLogout` (→ home). CTAP-1129.
        onDone: [
          {
            guard: ({ context }) => context.logoutReason === 'expired',
            actions: ['unsetUserContext', 'notifyUnauthenticated'],
            target: 'loggedOut.afterRefreshFailure',
          },
          {
            actions: ['unsetUserContext', 'notifyUnauthenticated'],
            target: 'loggedOut.afterLogout',
          },
        ],
        onError: [
          {
            guard: ({ context }) => context.logoutReason === 'expired',
            actions: [
              'unsetUserContext',
              'setIgnoreRefreshToken',
              'notifyUnauthenticated',
            ],
            target: 'loggedOut.afterRefreshFailure',
          },
          {
            actions: [
              'unsetUserContext',
              'setIgnoreRefreshToken',
              'notifyUnauthenticated',
            ],
            target: 'loggedOut.afterLogout',
          },
        ],
      },
    },
    refreshing: {
      invoke: {
        src: 'refreshAccessToken',
        input: ({ context }) => {
          return {
            accessToken: context.accessToken,
            endpoint: {
              client: context.endpoints.refresh,
              server: context.endpoints.ssrRefresh,
            },
            refreshToken: context.refreshToken,
            transport: context.transport,
          };
        },
        onDone: [
          {
            actions: [
              'unsetRefreshToken',
              'setAccessToken',
              'notifyAuthenticated',
            ],
            guard: () => typeof window !== 'undefined',
            target: 'loggedIn',
          },
          /**
           * We must transition to transient loggedInReady state from server-side refresh
           * so the frontend can send RESTORE event. Otherwise actions and timers from
           * loggedIn state won't fire properly from client (known issue in xstate)
           */
          {
            actions: ['unsetRefreshToken', 'setAccessToken'],
            target: 'loggedInReady',
          },
        ],
        /**
         * @todo
         * confirm this logic, does it make sense to unset refresh token
         * if the request fails for some reason on server side?
         */
        onError: [
          // logout can only happen on the client side, so we
          // guard the action to prevent it from happening on the server
          // and fall back to loggedOut state server-side
          {
            // Client-side refresh failure = an EXPIRED session, not a
            // deliberate sign-out; mark it so `unauthenticating` routes it to
            // afterRefreshFailure (return-path), not home (CTAP-1129).
            actions: ['unsetRefreshToken', 'markExpiredSession'],
            target: 'unauthenticating',
            guard: () => typeof window !== 'undefined',
          },
          {
            actions: ['unsetRefreshToken', 'notifyUnauthenticated'],
            target: 'loggedOut.afterRefreshFailure',
          },
        ],
      },
    },
    /**
     * `loggedOut` is a compound state. The substate records *why* we
     * are logged out so consumers can react appropriately — most
     * importantly, so a failed login attempt on an inline auth form
     * (e.g. /checkout step 1) does not trigger the same hard reload
     * that a real logout does, which would destroy the form's error
     * state before the user sees it.
     *
     * Transition matrix:
     *   initializing      → loggedOut.initial              (cold load, no token)
     *   authenticating    → loggedOut.afterFailedAuth      (wrong password)
     *   unauthenticating  → loggedOut.afterLogout          (DELIBERATE user logout — logoutReason 'deliberate')
     *   unauthenticating  → loggedOut.afterRefreshFailure  (client refresh failure — logoutReason 'expired', CTAP-1129)
     *   refreshing (SSR)  → loggedOut.afterRefreshFailure  (no client window to fall through unauthenticating)
     *
     * Existing consumer code that uses `state.matches('loggedOut')`
     * still matches every substate (xstate parent matching). Code
     * that uses `state.value === 'loggedOut'` will need to migrate to
     * `.matches()` because compound `value` is `{ loggedOut: '<sub>' }`.
     */
    loggedOut: {
      initial: 'initial',
      on: {
        LOGIN: {
          target: 'authenticating',
        },
        CLEAR_ERRORS: {
          actions: 'clearErrors',
        },
      },
      states: {
        initial: {},
        afterFailedAuth: {},
        afterLogout: {},
        afterRefreshFailure: {},
      },
    },
    /**
     * loggedInReady transient state is a workaround to restoring the snapshot on client side,
     * since actions won't run (they are assumed to have run already) and timers are stopped (they
     * have no clock mechanism to determine at what point to restart)
     */
    loggedInReady: {
      on: {
        RESTORE: {
          guard: () => typeof window !== 'undefined',
          target: 'loggedIn',
        },
      },
    },
    loggedIn: {
      after: {
        // 14min (840000ms), jwt access token expires in 15min
        // 840000: {
        refresh: {
          guard: () => typeof window !== 'undefined',
          target: 'refreshing',
        },
      },
      on: {
        LOGOUT: {
          // Deliberate sign-out → mark it so `unauthenticating` routes to
          // afterLogout (home), distinct from an expired session (CTAP-1129).
          actions: 'markDeliberateLogout',
          target: 'unauthenticating',
        },
        // A server 401 on an authenticated request: same teardown as LOGOUT,
        // but marked EXPIRED so the visitor is returned to where they were via
        // the login page instead of being dropped on the home page (CTAP-1425).
        SESSION_EXPIRED: {
          actions: 'markExpiredSession',
          target: 'unauthenticating',
        },
        CLEAR_ERRORS: {
          actions: 'clearErrors',
        },
        LOGIN: {
          target: 'authenticating',
        },
        REFRESH_TOKEN: {
          target: 'refreshing',
        },
        // Stay signed in after an email/password change: swap in the re-issued access token
        // (self-transition, no re-auth). The rotated refresh cookie was set on the mutation response.
        // `reenter` matters: a targetless self-transition keeps the REVOKED
        // token's `after: refresh` schedule, so the first background refresh
        // after a credential change fired on the old token's clock. Re-entering
        // restarts the 14-minute timer for the re-issued token (CTAP-1159).
        SESSION_REISSUED: {
          actions: ['setReissuedAccessToken', 'notifyReissued'],
          target: 'loggedIn',
          reenter: true,
        },
        // AFTER_LOGGED_IN: {
        //   target: 'afterLoggedIn',
        // },
      },
    },
    // onLogin: {
    //   always: {
    //     target: 'loggedIn',
    //   },
    // },
    // afterLoggedOut: {
    //   always: {
    //     target: 'loggedOut',
    //   },
    // },
  },
});

export default authMachine;
